import React from "react"
import styled from "styled-components"
import CloudinaryImage from "components/cloudinary-image"
import Icon from "icons"

import { minWidth, when } from "utils"

export const Container = styled.div`
  display: flex;
  flex-direction: column;

  padding: 3rem 0 3rem 4.5rem;

  ${minWidth.tablet`
    padding: 6rem 0 6rem 4rem;
  `}
`

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  width: 100%;
  padding-right: 3rem;

  ${minWidth.tablet`
    max-width: 50%;
  `};

  ${minWidth.desktop`
    max-width: 48rem;
  `};

  ${minWidth.desktopXL`
    max-width: 55rem;
  `};
`

export const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-bottom: 0.05rem solid #000;
`

export const Title = styled.h2`
  font-family: Maax, "Helvetica Neue", Helvetica, Arial, sans-serif;
  text-transform: lowercase;
  font-size: 3rem;
  letter-spacing: -0.02rem;
  font-weight: 400;
  color: #12233f;
  padding: 1rem 0;
  margin: 0;
  flex: 1;

  ${minWidth.tablet`
    font-size: 3.2rem;
    padding: 1.5rem 0;
  `};

  ${minWidth.desktop`
    font-size: 3.4rem;
    padding: 2rem 0;
  `};
`

export const ArrowContainer = styled.div`
  display: flex;
  gap: 3rem;
  align-items: center;

  ${minWidth.tablet`
    display: none;
  `};
`

const ArrowIcon = styled(Icon)`
  cursor: pointer;
  outline: none;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: color 0.2s ease;

  &:hover {
    color: #000;
  }

  ${when("disabled")`
    color: ${({ theme }) => (theme.component?.isDarkBackground ? "#aaa" : "rgba(34, 34, 34, 0.2)")};
    pointer-events: none;
  `}
`

export const ArrowLeft = (props) => (
  <ArrowIcon
    id="arrow-shop"
    width={16}
    height={17}
    style={{ transform: "rotate(180deg)" }}
    {...props}
  />
)

export const ArrowRight = (props) => <ArrowIcon id="arrow-shop" width={16} height={17} {...props} />

export const Text = styled.p`
  font-weight: 400;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.text.primary};
  letter-spacing: 0;
  margin: 0;

  ${minWidth.tablet`
    font-size: 1.6rem;
  `};

  ${minWidth.desktop`
    font-size: 1.9rem;
  `};
`

export const Cards = styled.div`
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-auto-flow: column;
  gap: 2rem 1.5rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0 2rem 0 0;
  margin: 0 0 0 -2rem;

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  ${when("showClassTags")`
    gap: 4rem 1.5rem;
  `}

  ${minWidth.tablet`
    gap: 3.5rem 1.5rem;
    margin: 2rem 0;
    padding: 0 2rem;

    ${when("showClassTags")`
      gap: 5.5rem 2rem;
    `}
  `};

  ${minWidth.desktop`
    gap: 4.5rem 1.5rem;

    ${when("showClassTags")`
      gap: 6.8rem 3rem;
    `}
  `};
`

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-width: 9.5rem;
  gap: 1rem;

  ${minWidth.tablet`
    min-width: 15rem;
  `};

  ${minWidth.desktop`
    min-width: 17.7rem;
  `};
`

export const CardImage = styled(CloudinaryImage)`
  width: 9.5rem;
  height: 9.5rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #d9d9d9;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${minWidth.tablet`
    width: 14.5rem;
    height: 14.5rem;
    border-radius: 1rem;
  `};

  ${minWidth.desktop`
    width: 17.6rem;
    height: 17.6rem;
  `};
`

export const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;

  ${minWidth.tablet`
    gap: 1.5rem;
  `};
`

export const CardName = styled.h3`
  font-size: 1rem;
  line-height: 1.32rem;
  font-weight: 500;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.1rem;

  ${minWidth.tablet`
    font-size: 1.2rem;
    line-height: 1.44rem;
  `};

  ${minWidth.desktop`
    font-size: 1.4rem;
    line-height: 1.7rem;
  `};
`

export const ClassTitlesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const CardClassTitle = styled.p`
  font-weight: 500;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.039rem;
  padding: 0.4rem;
  width: 100%;
  border-radius: 0.31rem;
  background-color: ${({ color }) => color};
  color: #fff;
  margin: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;

  ${minWidth.mobile`
    font-size: 0.63rem;
  `};

  ${minWidth.tablet`
    font-size: 0.9rem;
    border-radius: 0.4rem;
    letter-spacing: 0.04rem;
  `};

  ${minWidth.desktop`
    font-size: 1rem;
    padding: 0.5rem;
    letter-spacing: 0.05rem;
  `};

  ${minWidth.desktopL`
    font-size: 1.1rem;
  `};

  ${minWidth.desktopXL`
    font-size: 1.2rem;
  `};
`
